import React, { useContext, Component } from 'react';
import { motion } from 'framer-motion';
import { XIcon, CheckCircleIcon, AlertCircleIcon, AlertTriangleIcon, InfoIcon } from 'lucide-react';
import { NotificationProps, NotificationType } from '../../context/NotificationContext';
import { LanguageContext } from '../../App';
interface NotificationComponentProps extends Omit<NotificationProps, 'variant'> {
  onDismiss: () => void;
}
export const Notification: React.FC<NotificationComponentProps> = ({
  title,
  message,
  type = 'info',
  onDismiss,
  onAction,
  actionText,
  isOpen = true
}) => {
  const {
    language
  } = useContext(LanguageContext);
  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon className="w-5 h-5" data-id="element-747" />;
      case 'error':
        return <AlertCircleIcon className="w-5 h-5" data-id="element-748" />;
      case 'warning':
        return <AlertTriangleIcon className="w-5 h-5" data-id="element-749" />;
      case 'info':
        return <InfoIcon className="w-5 h-5" data-id="element-750" />;
      default:
        return null;
    }
  };
  const getBorderColor = (): string => {
    switch (type) {
      case 'success':
        return 'border-l-green-500';
      case 'error':
        return 'border-l-red-500';
      case 'warning':
        return 'border-l-yellow-500';
      case 'info':
        return 'border-l-congress-blue-500';
      case 'neutral':
        return 'border-l-gray-500';
      default:
        return 'border-l-gray-500';
    }
  };
  const getIconColor = (): string => {
    switch (type) {
      case 'success':
        return 'text-green-500';
      case 'error':
        return 'text-red-500';
      case 'warning':
        return 'text-yellow-500';
      case 'info':
        return 'text-congress-blue-500';
      case 'neutral':
        return 'text-gray-500';
      default:
        return 'text-gray-500';
    }
  };
  if (!isOpen) return null;
  return <motion.div className={`bg-white dark:bg-gray-800 border-l-4 ${getBorderColor()} rounded-md shadow-md pointer-events-auto max-w-md w-full overflow-hidden`} initial={{
    opacity: 0,
    x: 50,
    scale: 0.9
  }} animate={{
    opacity: 1,
    x: 0,
    scale: 1
  }} exit={{
    opacity: 0,
    x: 50,
    scale: 0.9
  }} transition={{
    duration: 0.2
  }} data-id="element-751">
      <div className="p-4" data-id="element-752">
        <div className="flex items-center justify-between" data-id="element-753">
          <div className="flex items-center" data-id="element-754">
            <span className={`mr-3 ${getIconColor()}`} data-id="element-755">{getIcon()}</span>
            <p className="font-medium text-gray-900 dark:text-white" data-id="element-756">{title}</p>
          </div>
          <button onClick={onDismiss} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-50 rounded-full p-1" aria-label={language === 'en' ? 'Close' : 'إغلاق'} data-id="element-757">
            <XIcon className="w-4 h-4" data-id="element-758" />
          </button>
        </div>
        {message && <p className="mt-2 text-sm text-gray-600 dark:text-gray-300" data-id="element-759">
            {message}
          </p>}
        {actionText && onAction && <div className="mt-3" data-id="element-760">
            <button onClick={onAction} className={`text-sm font-medium ${getIconColor()} hover:underline focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-50 rounded px-1`} data-id="element-761">
              {actionText}
            </button>
          </div>}
      </div>
    </motion.div>;
};